import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CartButton.css";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "email",
  "userEmail",
  "user_email",
  "fullName",
  "name",
  "userName",
  "username",
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();

  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: { source: "CartButton", action: "clear-auth" },
    })
  );
};

const getLocalCartCount = () => {
  try {
    const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
    return Array.isArray(storedCart) ? storedCart.length : 0;
  } catch {
    return 0;
  }
};

const saveCartToLocal = (cartItems = []) => {
  try {
    localStorage.setItem(
      "cart",
      JSON.stringify(Array.isArray(cartItems) ? cartItems : [])
    );
  } catch {
    // localStorage may be blocked in some browsers
  }
};

const CartButton = () => {
  const navigate = useNavigate();

  const [cartCount, setCartCount] = useState(() => getLocalCartCount());
  const [isRealtimeOnline, setIsRealtimeOnline] = useState(false);

  const mountedRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastFetchAtRef = useRef(0);

  /* ==============================
     SAFE FETCH CART COUNT
     IMPORTANT:
     - No cartUpdated dispatch inside this function
     - Prevents loop
     - Prevents parallel API calls
     - Adds small cooldown
  ============================== */
  const fetchCartCount = useCallback(async ({ force = false } = {}) => {
    const token = getToken();

    if (!token) {
      saveCartToLocal([]);
      if (mountedRef.current) {
        setCartCount(0);
      }
      return;
    }

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchAtRef.current;

    if (!force && timeSinceLastFetch < 1200) {
      return;
    }

    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;
    lastFetchAtRef.current = now;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(`${API_BASE}/domains/cart`, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        saveCartToLocal([]);

        if (mountedRef.current) {
          setCartCount(0);
        }

        clearAuth();
        return;
      }

      if (response.status === 429) {
        if (mountedRef.current) {
          setCartCount(getLocalCartCount());
        }
        return;
      }

      if (!response.ok || data?.success === false) {
        if (mountedRef.current) {
          setCartCount(getLocalCartCount());
        }
        return;
      }

      const backendCart = Array.isArray(data?.data) ? data.data : [];

      saveCartToLocal(backendCart);

      if (mountedRef.current) {
        setCartCount(backendCart.length);
      }
    } catch (error) {
      if (error?.name !== "AbortError" && mountedRef.current) {
        setCartCount(getLocalCartCount());
      }
    } finally {
      fetchInProgressRef.current = false;
    }
  }, []);

  /* ==============================
     DEBOUNCED REFRESH
  ============================== */
  const scheduleCartRefresh = useCallback(
    ({ force = false, delay = 500 } = {}) => {
      clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(() => {
        fetchCartCount({ force });
      }, delay);
    },
    [fetchCartCount]
  );

  /* ==============================
     INITIAL + EVENTS
  ============================== */
  useEffect(() => {
    mountedRef.current = true;

    fetchCartCount({ force: true });

    const handleStorage = (event) => {
      if (!event || event.key === "cart") {
        setCartCount(getLocalCartCount());
      }
    };

    const handleCartUpdated = (event) => {
      const source = event?.detail?.source || "";

      if (source === "CartButton") {
        return;
      }

      setCartCount(getLocalCartCount());
      scheduleCartRefresh({ force: true, delay: 600 });
    };

    const handleAuthState = () => {
      scheduleCartRefresh({ force: true, delay: 300 });
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("cartUpdated", handleCartUpdated);
    window.addEventListener("auth-state-changed", handleAuthState);

    return () => {
      mountedRef.current = false;

      clearTimeout(debounceTimerRef.current);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("cartUpdated", handleCartUpdated);
      window.removeEventListener("auth-state-changed", handleAuthState);
    };
  }, [fetchCartCount, scheduleCartRefresh]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const token = getToken();

    if (!token) {
      setIsRealtimeOnline(false);
      return undefined;
    }

    const socket = connectRealtimeSocket();

    if (!socket) {
      setIsRealtimeOnline(false);
      return undefined;
    }

    const softRefresh = () => {
      scheduleCartRefresh({ force: false, delay: 800 });
    };

    const handleConnected = () => {
      setIsRealtimeOnline(true);
      scheduleCartRefresh({ force: true, delay: 500 });
    };

    const handleDisconnected = () => {
      setIsRealtimeOnline(false);
    };

    const handleSocketAuthError = () => {
      setIsRealtimeOnline(false);
      saveCartToLocal([]);
      setCartCount(0);
      clearAuth();
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("domain:status", softRefresh);
    socket.on("domain:availability:update", softRefresh);
    socket.on("payment:status", softRefresh);
    socket.on("dashboard:update", softRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("domain:status", softRefresh);
      socket.off("domain:availability:update", softRefresh);
      socket.off("payment:status", softRefresh);
      socket.off("dashboard:update", softRefresh);

      socket.off("connect_error", handleSocketAuthError);
    };
  }, [scheduleCartRefresh]);

  const handleOpenCart = () => {
    navigate("/cart");
  };

  return (
    <button
      type="button"
      className={`cart-btn ${isRealtimeOnline ? "cart-online" : ""}`}
      aria-label={`Open cart, ${cartCount} item${cartCount === 1 ? "" : "s"}`}
      onClick={handleOpenCart}
      title="Open Cart"
    >
      <span className="cart-icon">🛒</span>

      {cartCount > 0 ? <span className="cart-badge">{cartCount}</span> : null}

      <span className="cart-live-dot" aria-hidden="true" />
    </button>
  );
};

export default CartButton;